import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  UserView,
  UserRulesInput,
  CompanyRulesView,
  UserAttendanceImageConfig,
  UserFaceRecognitionConfig,
  UserLeaveWithoutScheduleConfig,
  UserShiftSwapConfig,
  UserShiftSwapAutoApproveConfig,
} from "../../../../models/general";
import { WorkplaceConfig } from "../../../../models/attendance";
import { getUserRules, updateUserRules } from "../../../../api/General/account";
import { getCompanyRules } from "../../../../api/General/Company";
import { Card, CardContent } from "../../../../components/ui/Card";
import StickyActionBar from "../../../../components/ui/StickyActionBar";
import { ErrorState } from "../../../../components/AsyncStates";
import { Skeleton } from "../../../../components/ui/Skeleton";
import { useToast, ToastRenderer } from "../../../../hooks/useToast";
import { extractApiError } from "../../../../utils/errors";
import { RootState } from "../../../../store/redux/store/store";

interface UserRulesForm {
  attendanceImageConfig: string;
  faceRecognitionConfig: string;
  workplaceConfig: string;
  leaveWithoutScheduleConfig: string;
  shiftSwapConfig: string;
  shiftSwapAutoApproveConfig: string;
  userId: string;
}

const DEFAULT_FORM: UserRulesForm = {
  attendanceImageConfig: UserAttendanceImageConfig.InheritedFromCompany,
  faceRecognitionConfig: UserFaceRecognitionConfig.InheritedFromCompany,
  workplaceConfig: WorkplaceConfig.InheritedFromCompany as unknown as string,
  leaveWithoutScheduleConfig: UserLeaveWithoutScheduleConfig.InheritedFromCompany,
  shiftSwapConfig: UserShiftSwapConfig.InheritedFromCompany,
  shiftSwapAutoApproveConfig: UserShiftSwapAutoApproveConfig.InheritedFromCompany,
  userId: "",
};

const INHERIT = "InheritedFromCompany";

const segBtn = (active: boolean) =>
  `px-3 py-1.5 rounded-md text-sm font-medium border transition-colors disabled:opacity-50 ${
    active
      ? "border-primary bg-primary/10 text-primary"
      : "border-border bg-background text-muted-foreground hover:border-primary/50"
  }`;

interface OverrideOption { value: string; label: string; }

const OverrideField: React.FC<{
  label: string;
  description?: string;
  value: string;
  options: OverrideOption[];
  onChange: (v: string) => void;
  disabled?: boolean;
  inheritNote?: string;
}> = ({ label, description, value, options, onChange, disabled, inheritNote }) => (
  <div className="flex flex-col gap-2">
    <div className="flex flex-col gap-0.5">
      <span className="text-sm font-medium text-foreground">{label}</span>
      {description && <span className="text-xs text-muted-foreground">{description}</span>}
    </div>
    <div className="flex flex-wrap gap-2" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          disabled={disabled}
          onClick={() => onChange(o.value)}
          className={segBtn(value === o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
    {inheritNote && value === INHERIT && (
      <p className="text-xs text-muted-foreground">{inheritNote}</p>
    )}
  </div>
);

interface IProps {
  refreshParent: () => void;
  userInView?: UserView;
}

const RulesManagement: React.FC<IProps> = ({ refreshParent, userInView }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const fallbackCompanyId = useSelector((state: RootState) => state.user.companyId);
  const companyId = userInView?.company?.id ?? fallbackCompanyId ?? undefined;
  const { toast, showToast, dismissToast } = useToast();

  const [form, setForm] = useState<UserRulesForm>(DEFAULT_FORM);
  const [snapshot, setSnapshot] = useState<UserRulesForm>(DEFAULT_FORM);
  const [companyDefaults, setCompanyDefaults] = useState<CompanyRulesView | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const faceRecognitionEnabled = Boolean(userInView?.company?.faceRecognitionEnabled);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const userRules = await getUserRules(id ?? "");
      if (userRules.errors.length > 0) {
        setLoadError(userRules.errors[0]?.description || t('Failed to load user rules'));
        return;
      }
      const p = userRules.payload;
      // A user with no override row yet returns a null payload — show all-inherited defaults instead of
      // crashing on a null dereference (rule #4: check the result before using the payload).
      const loaded: UserRulesForm = p
        ? {
            attendanceImageConfig: p.attendanceImageConfig,
            faceRecognitionConfig: p.faceRecognitionConfig,
            workplaceConfig: p.workplaceConfig as unknown as string,
            leaveWithoutScheduleConfig: p.leaveWithoutScheduleConfig,
            shiftSwapConfig: p.shiftSwapConfig,
            shiftSwapAutoApproveConfig: p.shiftSwapAutoApproveConfig,
            userId: p.userId,
          }
        : { ...DEFAULT_FORM, userId: id ?? "" };
      setForm(loaded);
      setSnapshot(loaded);

      // Company defaults power the "inherited value" hints. A failure here must not block editing,
      // so it degrades to no hints rather than an error.
      if (companyId != null) {
        try {
          const rules = await getCompanyRules(companyId);
          if (rules.errors.length === 0) setCompanyDefaults(rules.payload);
        } catch {
          /* hints unavailable — non-fatal */
        }
      }
    } catch (e) {
      setLoadError(extractApiError(e, t('Failed to load user rules')));
    } finally {
      setLoading(false);
    }
  }, [id, companyId, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const hasUnsavedChanges = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(snapshot),
    [form, snapshot],
  );

  const save = async () => {
    setSaving(true);
    try {
      await updateUserRules(form.userId, form as unknown as UserRulesInput);
      setSnapshot(form);
      showToast(t('User Rules Updated Successfully'), 'success');
      refreshParent();
    } catch (e) {
      showToast(extractApiError(e, t('Update failed')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const resetToSaved = () => setForm(snapshot);

  const resetAllToInherited = () => setForm((f) => ({
    ...f,
    attendanceImageConfig: INHERIT,
    faceRecognitionConfig: INHERIT,
    workplaceConfig: WorkplaceConfig.InheritedFromCompany as unknown as string,
    leaveWithoutScheduleConfig: INHERIT,
    shiftSwapConfig: INHERIT,
    shiftSwapAutoApproveConfig: INHERIT,
  }));

  const set = (patch: Partial<UserRulesForm>) => setForm((f) => ({ ...f, ...patch }));

  // Company-default resolution for the inherit hints.
  const requiredLabel = (v?: string) => (v === 'Required' ? t('Required') : t('Not required'));
  const enabledLabel = (b?: boolean) => (b ? t('Enabled') : t('Disabled'));
  const allowedLabel = (b?: boolean) => (b ? t('Allowed') : t('Not allowed'));
  // nsSeparator:false — this key contains a ':' which i18next would otherwise treat as a namespace
  // separator and split, dropping the "Using company default:" prefix.
  const usingDefault = (value: string) => t('Using company default: {{value}}', { value, nsSeparator: false });

  if (loadError) {
    return (
      <div className="py-12">
        <ErrorState message={loadError} onRetry={fetchData} retryText={t('Try again')} title={t('Could not load rules')} />
        <ToastRenderer toast={toast} onDismiss={dismissToast} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-4" aria-busy="true" aria-live="polite">
        <Skeleton className="h-6 w-56 rounded" />
        <div className="bg-card border border-border rounded-xl p-4 space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-48 rounded" />
              <Skeleton className="h-8 w-72 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap items-start justify-between gap-3 pb-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold text-foreground">{t('User Attendance Rules')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('Override the company defaults for this employee. Left on Inherit, each rule follows the company setting.')}
          </p>
        </div>
        <button
          type="button"
          onClick={resetAllToInherited}
          disabled={saving}
          className="text-sm font-medium text-primary hover:underline disabled:opacity-50"
        >
          {t('Reset all to company defaults')}
        </button>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-col gap-3">
          {/* Attendance capture */}
          <div className="flex flex-col gap-1">
            <h3 className="text-base font-semibold text-foreground">{t('Attendance capture')}</h3>
            <p className="text-sm text-muted-foreground">{t('Photo and face-match requirements when this employee clocks in.')}</p>
          </div>

          <OverrideField
            label={t('Attendance Image Rule')}
            description={t('Whether a photo is required at clock-in for this employee.')}
            value={form.attendanceImageConfig}
            onChange={(v) => set({ attendanceImageConfig: v })}
            disabled={saving}
            options={[
              { value: UserAttendanceImageConfig.InheritedFromCompany, label: t('Inherit') },
              { value: UserAttendanceImageConfig.Required, label: t('Required') },
              { value: UserAttendanceImageConfig.NotRequired, label: t('Not required') },
            ]}
            inheritNote={companyDefaults ? usingDefault(requiredLabel(companyDefaults.attendanceImageConfig as unknown as string)) : undefined}
          />

          {faceRecognitionEnabled && (
            <OverrideField
              label={t('Face Recognition Rule')}
              description={t('Whether a face match is required at clock-in for this employee.')}
              value={form.faceRecognitionConfig}
              onChange={(v) => set({ faceRecognitionConfig: v })}
              disabled={saving}
              options={[
                { value: UserFaceRecognitionConfig.InheritedFromCompany, label: t('Inherit') },
                { value: UserFaceRecognitionConfig.Required, label: t('Required') },
                { value: UserFaceRecognitionConfig.NotRequired, label: t('Not required') },
              ]}
              inheritNote={companyDefaults ? usingDefault(requiredLabel(companyDefaults.faceRecognitionConfig as unknown as string)) : undefined}
            />
          )}

          <hr className="border-border my-1" />

          {/* Location */}
          <div className="flex flex-col gap-1">
            <h3 className="text-base font-semibold text-foreground">{t('Location')}</h3>
            <p className="text-sm text-muted-foreground">{t('Where this employee is allowed to clock in.')}</p>
          </div>

          <OverrideField
            label={t('Workplace Config')}
            description={t('Inherit the company default, restrict clock-in to configured workplaces, or allow it from any location.')}
            value={form.workplaceConfig}
            onChange={(v) => set({ workplaceConfig: v })}
            disabled={saving}
            options={[
              { value: WorkplaceConfig.InheritedFromCompany as unknown as string, label: t('Inherit') },
              { value: WorkplaceConfig.CompanyOrUserSpecific as unknown as string, label: t('Company / user workplaces') },
              { value: WorkplaceConfig.AnyLocation as unknown as string, label: t('Any location') },
            ]}
          />

          <hr className="border-border my-1" />

          {/* Leave */}
          <div className="flex flex-col gap-1">
            <h3 className="text-base font-semibold text-foreground">{t('Leave')}</h3>
            <p className="text-sm text-muted-foreground">{t('Leave behaviour specific to this employee.')}</p>
          </div>

          <OverrideField
            label={t('Leave Without Schedule')}
            description={t('Allow this employee to request leave even with no assigned working schedule.')}
            value={form.leaveWithoutScheduleConfig}
            onChange={(v) => set({ leaveWithoutScheduleConfig: v })}
            disabled={saving}
            options={[
              { value: UserLeaveWithoutScheduleConfig.InheritedFromCompany, label: t('Inherit') },
              { value: UserLeaveWithoutScheduleConfig.Allowed, label: t('Allowed') },
              { value: UserLeaveWithoutScheduleConfig.NotAllowed, label: t('Not allowed') },
            ]}
            inheritNote={companyDefaults ? usingDefault(allowedLabel(companyDefaults.allowLeaveWithoutSchedule)) : undefined}
          />

          <hr className="border-border my-1" />

          {/* Shift swaps */}
          <div className="flex flex-col gap-1">
            <h3 className="text-base font-semibold text-foreground">{t('Shift swaps')}</h3>
            <p className="text-sm text-muted-foreground">{t('Whether this employee can trade shifts, and how swaps are approved.')}</p>
          </div>

          <OverrideField
            label={t('Shift Swap')}
            description={t('Allow this employee to request and accept shift swaps.')}
            value={form.shiftSwapConfig}
            onChange={(v) => set({ shiftSwapConfig: v })}
            disabled={saving}
            options={[
              { value: UserShiftSwapConfig.InheritedFromCompany, label: t('Inherit') },
              { value: UserShiftSwapConfig.Enabled, label: t('Enabled') },
              { value: UserShiftSwapConfig.Disabled, label: t('Disabled') },
            ]}
            inheritNote={companyDefaults ? usingDefault(enabledLabel(companyDefaults.shiftSwapEnabled)) : undefined}
          />

          <OverrideField
            label={t('Auto-apply Shift Swaps')}
            description={t('Apply swaps for this employee immediately on acceptance, skipping manager approval (both parties must have it on).')}
            value={form.shiftSwapAutoApproveConfig}
            onChange={(v) => set({ shiftSwapAutoApproveConfig: v })}
            disabled={saving}
            options={[
              { value: UserShiftSwapAutoApproveConfig.InheritedFromCompany, label: t('Inherit') },
              { value: UserShiftSwapAutoApproveConfig.Enabled, label: t('Enabled') },
              { value: UserShiftSwapAutoApproveConfig.Disabled, label: t('Disabled') },
            ]}
            inheritNote={companyDefaults ? usingDefault(enabledLabel(companyDefaults.shiftSwapAutoApproveOnPeerAccept)) : undefined}
          />
        </CardContent>
      </Card>

      <StickyActionBar
        hasUnsavedChanges={hasUnsavedChanges}
        isSaving={saving}
        onReset={resetToSaved}
        onSave={save}
        t={t}
      />

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

export default RulesManagement;
